import React from "react";
import { useState, useMemo, useEffect } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { useApplication } from "../api";
import { Col, Skeleton } from "antd";
import RadioCardFormItem from "../components/RadioCardFormItem";
import { ResponseFields } from "@dreambigger/shared/src/types";
import { useAuthToken } from "../passwordless-auth";
import { api, TruStageApi } from "@dreambigger/shared/src/api/acquire";

export interface TruStageResponse {
  payLoad?: {
    products?: {
      productCode?: string;
      productName?: string;
      headline?: string;
      shortDescription?: string;
      longDescription?: string;
      footnote?: string;
      desktopBannerImageURL?: string;
      desktopModalImageURL?: string;
      mobileBannerImageURL?: string;
      mobileModalImageURL?: string;
      darkProductIconURL?: string;
      lightProductIconURL?: string;
      primaryColor?: string;
      secondaryColor?: string;
      quotes?: {
        option?: string;
        loanAmountFinanced?: number;
        loanApr?: number;
        costPerPayment?: number;
        totalCost?: number;
        ratePerThousand?: number | null;
        loanTerm?: number;
        footNote?: string;
      }[];
    }[];
  };
  messages?: string[];
}

const BORDER_COLOR = "#bfbfbf";

const TruStage = ({ flow, step, brand, progress }: StepProps) => {
  const [disabled, setDisabled] = useState(true);
  const [truStage, setTruStage] = useState<{
    response: TruStageResponse | undefined;
    isValidating: boolean;
  }>({
    response: undefined,
    isValidating: true,
  });
  const [form] = Form.useForm();
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [application, flow, step]
  );

  const tokenHelper = useAuthToken(flow.financialInstitution.id);

  // Initialize the various api's we need for this step.
  const truStageApi = new TruStageApi(api, tokenHelper);

  // On receiving fresh data (such as on a step transition), disable the next button and reset the form.
  useEffect(() => {
    setDisabled(true);
    form.resetFields();
  }, [step, response]);

  useEffect(() => {
    const fetchTruStageResponse = async () => {
      if (!application || !step) return;
      try {
        const { data } = await truStageApi.getProducts({
          onboardingApplicationId: application.id,
        });
        setTruStage({ response: data, isValidating: false });
        // if there are no products returned, enable
        if (!data.payLoad?.products?.length) {
          setDisabled(false);
        }
      } catch (error) {
        setTruStage({ response: undefined, isValidating: false });
        console.error("Error loading protection products:", error);
        // Protection products are an optional upsell — if the call fails,
        // let the applicant proceed rather than blocking on a disabled button.
        setDisabled(false);
      }
    };
    fetchTruStageResponse();
  }, [step, application]);

  const checkDisabled = (values: ResponseFields) => {
    const hasUndefined = Object.values(values).some(
      (value) => value === undefined
    );
    setDisabled(hasUndefined);
  };

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
  };

  const sortProducts = (products: any[]) => {
    // We want to sort the products in a specific order.
    const order = ["CI", "DP", "GAP", "MRC"];

    return products.sort((a, b) => {
      const codeA = a.productCode?.toUpperCase() || "";
      const codeB = b.productCode?.toUpperCase() || "";

      const indexA = order.indexOf(codeA);
      const indexB = order.indexOf(codeB);

      // Sort based on the custom order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) {
        return -1;
      }
      if (indexB !== -1) {
        return 1;
      }

      // Sort alphabetically for remaining codes.
      return codeA.localeCompare(codeB);
    });
  };

  // ----- RENDER ------
  // Return nothing if there is no application.
  if (!application) {
    return null;
  }

  // While the response is being fetched, show a loading skeleton.
  if (truStage.isValidating) {
    return (
      <StepWrapper {...wrapperProps}>
        <Col>
          <Skeleton.Input
            className="mb-2 w-100"
            active={true}
            style={{ height: "150px" }}
          />
          <Skeleton.Input
            className="w-100 mb-2"
            active={true}
            style={{ height: "200px" }}
          />
          <Skeleton.Input
            className="w-100"
            active={true}
            style={{ height: "125px" }}
          />
        </Col>
      </StepWrapper>
    );
  }

  const products = truStage.response?.payLoad?.products;

  // The products call failed or returned a malformed response. Protection
  // products are an optional upsell, so let the applicant continue.
  if (!products) {
    return (
      <StepWrapper {...wrapperProps}>
        <p>
          We could not load protection products right now, but you can continue
          with your application.
        </p>
      </StepWrapper>
    );
  }

  // Show a message if no products are returned.
  if (!products.length) {
    return (
      <StepWrapper {...wrapperProps}>
        <p>No protection products were found.</p>
      </StepWrapper>
    );
  }

  // Sort the products before rendering
  const sortedProducts = sortProducts(products);

  // If the response is valid, render the form.
  return (
    <StepWrapper {...wrapperProps}>
      {sortedProducts.map((product) => {
        const {
          productCode,
          productName,
          headline,
          shortDescription,
          longDescription,
          footnote,
          quotes,
        } = product;
        if (!productCode) return null;
        const callout = quotes?.[0]?.costPerPayment;
        const modalContent = (
          <>
            <p
              className="fwt f-4"
              dangerouslySetInnerHTML={{ __html: headline || "" }}
            />
            <p
              dangerouslySetInnerHTML={{
                __html: longDescription || "",
              }}
            />
            {footnote && (
              <div className="mt-6">
                <p className="f-2 fwb">Disclosures</p>
                <p
                  className="f-2 gray-8 mb-0"
                  dangerouslySetInnerHTML={{ __html: footnote }}
                ></p>
              </div>
            )}
          </>
        );

        return (
          <RadioCardFormItem
            key={productCode}
            className="mb-2"
            title={productName}
            titleColor={brand.stepTitleColor}
            callout={
              typeof callout === "number"
                ? `~$${callout.toFixed(2)}/month`
                : undefined
            }
            modalLaunchText="Learn More"
            description={shortDescription}
            modalTitle={
              <div
                className="bb pb-2 mb-5"
                style={{ borderColor: BORDER_COLOR }}
                dangerouslySetInnerHTML={{ __html: `About ${productName}` }}
              />
            }
            modalContent={modalContent}
            optionsLabel={`Are you interested in ${productName}?`}
            options={[
              {
                label: "Yes",
                description: "I’d like to learn more.",
                value: true,
                type: "yes",
              },
              {
                label: "No",
                description: "I’m not interested.",
                value: false,
                type: "no",
              },
            ]}
            form={form}
            fieldName={productCode}
            required
            onChange={checkDisabled}
          />
        );
      })}
    </StepWrapper>
  );
};

export default TruStage;
